// The header bell's own read (app-shell 2026-09-14, lever 2): the fields the overdue count
// (isOverdue), the deadline panel (utils/devOverdue, AUD-06) and the designer scoping
// read — of the developments that can be overdue at all. Stage 0 had every portal pay the
// 50-column list at boot (5 pages / 7.7 MB); the first cut of this hook read every active row
// (4,706 / 2.1 MB); this reads the 2,024 rows in a budgeted stage. Both predicates index
// STATUS_DAYS / DEFAULT_OVERDUE_RULES by stage_status, and only Drawing / Hand made / Color
// have an entry — a Done (or stageless) row is false in both before any date is read, so
// leaving it out server-side changes no number. Live stage values 2026-09-14: Done 2,584 ·
// Hand made 1,137 · Color 525 · Drawing 362 · null 98.
//
// One React Query keyed UNDER ['developments'] so the existing developments realtime (Tier A,
// useDevelopmentsRealtime) invalidates it by prefix — an edit anywhere refreshes the bell
// without a reload; reloadDevs invalidates it too. Enabled by App.js only once a portal is
// open (the Hub has no header, so no bell). Kept 5 min.
//
// Rows carry every field both top-level AND under `data`, flattened the way useAppData
// flattens the full list (normalizeStatus / normalizeStageStatus), because the readers are
// split: isOverdue reads dev.full_status, deadlineHelpers read data?.deadline_extension_days,
// the panel reads d.designer. Same rows, same predicates, same numbers — a projection.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { fetchAllPaged } from '../utils/pgIn';
import { normalizeStatus, normalizeStageStatus } from '../shared';
import { SELECTION_SEASON } from '../components/developments/SelectionChip';

export const DEADLINE_DEVS_KEY = ['developments', 'deadline-slim'];
const DATA_FIELDS = ['dev_number', 'designer', 'stage_status', 'full_status', 'status_set_at', 'dev_date', 'drawing_date', 'deadline_extension_days', 'deadline_snoozed_until', 'completedAt'];
// AUD-06 (gate J): stop_reason + collection_id (columns) and data completedAt feed the one overdue rule's
// guards (utils/devOverdue) — without them the slim rows would pass every guard unread.
// DEV-03: the plain-OUT rule (utils/devOverdue devIsPlainOut) reads the season's decision and decided_at —
// two scalars, the season key quoted as in the locked_since filter below, rebuilt by flatten into the same
// data.selections shape the full list carries (one shape for one rule).
const SEL = `data->selections->"${SELECTION_SEASON}"`;
export const DEADLINE_SELECT = ['id', 'stop_reason', 'collection_id', ...DATA_FIELDS.map((f) => `${f}:data->${f}`),
  `sel_decision:${SEL}->>decision`, `sel_decided_at:${SEL}->>decided_at`].join(',');
// the stages that carry no budget in STATUS_DAYS and no rule in DEFAULT_OVERDUE_RULES — both
// spellings normalizeStageStatus folds to Done. A NULL stage is dropped by NOT IN too (three-valued
// logic, the one place Conv #58's trap is the wanted behaviour): both predicates refuse it as well.
const NEVER_OVERDUE_STAGES = '("Done","done","Completed","completed")';

const flatten = (r) => {
  const data = {}; for (const f of DATA_FIELDS) data[f] = r[f] ?? null;
  data.selections = { [SELECTION_SEASON]: { decision: r.sel_decision ?? null, decided_at: r.sel_decided_at ?? null } };
  return { ...r, data, full_status: normalizeStatus(r.full_status), stage_status: normalizeStageStatus(r.stage_status) };
};

export async function fetchDeadlineDevs() {
  const rows = await fetchAllPaged(
    () => supabase.from('developments').select(DEADLINE_SELECT).is('deleted_at', null)
      .or('data->>archived.is.null,data->>archived.neq.true')                       // Conv #58: NULL is "not archived"
      .not('data->>stage_status', 'in', NEVER_OVERDUE_STAGES)
      .is(`data->selections->"${SELECTION_SEASON}"->>locked_since`, null)             // Collection C2: a locked dev never rings the bell (the slim read carries no selections, so the exclusion is the server's)
      .order('id'),                                                                 // a stable page order; the panel sorts by days over
    { label: 'deadline devs' }
  );
  return rows.map(flatten);
}

/** The developments that can be overdue, slim — [] until read. */
export function useDeadlineDevs(enabled = true) {
  const q = useQuery({ queryKey: DEADLINE_DEVS_KEY, queryFn: fetchDeadlineDevs, enabled, staleTime: 5 * 60 * 1000, gcTime: 30 * 60 * 1000 });
  return q.data || EMPTY;
}
const EMPTY = [];
